import React, { useMemo, useRef, useState, useEffect } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useSound } from '../../contexts/SiteContext';
import sceneConfig, { getPhonePosition } from '../../config/sceneConfig';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import LabelledToggle from './LabelledToggle';

const CameraAlignedLabels = ({ 
  visible = true, 
  depthOffset: propDepthOffset,
  leftChildren,
  rightChildren,
  horizontalOffset: propHorizontalOffset,
  verticalOffset: propVerticalOffset,
  sceneName,
  getBasePosition = null
}) => { 
  const { getSceneObject } = useCanvasContext();
  const { camera } = useThree();
  const { isSoundOn, toggleSound } = useSound();

  const breakpoint = useBreakpoint();

  // Helper to get responsive multiplier
  const responsiveMultiplier = useMemo(() => {
    const breakpoints = sceneConfig.alignedLabels?.responsive?.breakpoints;
    
    if (!breakpoints) {
      return { depth: 1.0, horizontal: 1, vertical: 1.0 };
    }

    const breakpointConfig = breakpoints[breakpoint];
    if (!breakpointConfig) {
      return { depth: 1.0, horizontal: 1, vertical: 1.0 };
    }

    return {
      depth: breakpointConfig.depthMultiplier || 1.0,
      horizontal: breakpointConfig.horizontalMultiplier || 1.0,
      vertical: breakpointConfig.verticalMultiplier || 1.0
    };
  }, [breakpoint]);

  // Calculate depthOffset from sceneConfig if not provided as prop
  const depthOffset = useMemo(() => {
    if (propDepthOffset !== undefined) {
      return propDepthOffset;
    }

    const labelConfig = sceneConfig.alignedLabels;
    if (!labelConfig) {
      return 10; // Fallback default
    }

    // Get scene-specific offset or use default
    const sceneKey = sceneName?.replace('-scene', '') || 'default';
    const sceneSettings = labelConfig.scenes?.[sceneKey];
    const defaultSettings = labelConfig.default || { depthOffset: 10 };
    
    const baseOffset = sceneSettings?.depthOffset ?? defaultSettings.depthOffset ?? 10;

    // Apply responsive multiplier
    return baseOffset * responsiveMultiplier.depth;
  }, [propDepthOffset, sceneName, responsiveMultiplier]);

  // Calculate horizontalOffset from sceneConfig if not provided as prop
  const horizontalOffset = useMemo(() => {
    if (propHorizontalOffset !== undefined) {
      return propHorizontalOffset;
    }

    const labelConfig = sceneConfig.alignedLabels;
    if (!labelConfig) {
      return 6.2; // Fallback default
    }

    // Get scene-specific offset or use default
    const sceneKey = sceneName?.replace('-scene', '') || 'default';
    const sceneSettings = labelConfig.scenes?.[sceneKey];
    const defaultSettings = labelConfig.default || { horizontalOffset: 6.2 };
    
    const baseOffset = sceneSettings?.horizontalOffset ?? defaultSettings.horizontalOffset ?? 6.2;

    // Apply responsive multiplier
    return baseOffset * responsiveMultiplier.horizontal;
  }, [propHorizontalOffset, sceneName, responsiveMultiplier]);

  // Calculate verticalOffset from sceneConfig if not provided as prop
  const verticalOffset = useMemo(() => {
    if (propVerticalOffset !== undefined) {
      return propVerticalOffset;
    }

    const labelConfig = sceneConfig.alignedLabels;
    if (!labelConfig) {
      return -1; // Fallback default
    }

    // Get scene-specific offset or use default
    const sceneKey = sceneName?.replace('-scene', '') || 'default';
    const sceneSettings = labelConfig.scenes?.[sceneKey];
    const defaultSettings = labelConfig.default || { verticalOffset: -1 };
    
    const baseOffset = sceneSettings?.verticalOffset ?? defaultSettings.verticalOffset ?? -1;

    // Apply responsive multiplier
    return baseOffset * responsiveMultiplier.vertical;
  }, [propVerticalOffset, sceneName, responsiveMultiplier]);

  const [leftPos, setLeftPos] = useState([0, 0, 0]);
  const [rightPos, setRightPos] = useState([0, 0, 0]);
  const [shouldRender, setShouldRender] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const leftGroupRef = useRef();
  const rightGroupRef = useRef();
  const lastUpdateRef = useRef(0);
  const updateThrottle = 16; // ~60fps max updates
  const previousVisibleRef = useRef(false); // Initialize to false so first render with visible=true triggers fade-in
  const hasShownRef = useRef(false); // Track if labels have already been shown

  // Get fade delays from sceneConfig
  const fadeDelays = useMemo(() => {
    const labelConfig = sceneConfig.alignedLabels;
    if (!labelConfig) {
      return { fadeInDelay: 500, fadeOutDelay: 500 };
    }

    const sceneKey = sceneName?.replace('-scene', '') || 'default';
    const sceneSettings = labelConfig.scenes?.[sceneKey];
    const defaultSettings = labelConfig.default || { fadeInDelay: 500, fadeOutDelay: 500 };

    return {
      fadeInDelay: sceneSettings?.fadeInDelay ?? defaultSettings.fadeInDelay ?? 500,
      fadeOutDelay: sceneSettings?.fadeOutDelay ?? defaultSettings.fadeOutDelay ?? 500
    };
  }, [sceneName]);

  // Handle fade in/out with proper timing
  // Only trigger fade-in when visible changes from false to true, not on every render
  useEffect(() => {
    const wasVisible = previousVisibleRef.current;
    const isVisible = visible;
    
    // Only trigger fade-in on transition from false to true
    if (isVisible && !wasVisible) {
      // First time showing - trigger fade-in
      setShouldShow(false);
      setShouldRender(true);
      hasShownRef.current = false;
      
      const showTimer = setTimeout(() => {
        setShouldShow(true);
        hasShownRef.current = true;
      }, fadeDelays.fadeInDelay);
      
      previousVisibleRef.current = isVisible;
      return () => clearTimeout(showTimer);
    } else if (isVisible && wasVisible && !hasShownRef.current) {
      // Already visible but fade-in hasn't completed yet - keep waiting
      // Don't reset shouldShow, let the existing timer complete
      return;
    } else if (isVisible && wasVisible && hasShownRef.current) {
      // Already visible and already shown - keep it visible without re-triggering animation
      setShouldRender(true);
      setShouldShow(true);
      return;
    } else if (!isVisible && wasVisible) {
      // Hiding - trigger fade-out (only if was previously visible)
      setShouldShow(false);
      hasShownRef.current = false;
      
      const hideTimer = setTimeout(() => {
        setShouldRender(false);
      }, fadeDelays.fadeOutDelay);
      
      previousVisibleRef.current = isVisible;
      return () => clearTimeout(hideTimer);
    }
    
    // Update ref for next render
    previousVisibleRef.current = isVisible;
  }, [visible, fadeDelays.fadeInDelay, fadeDelays.fadeOutDelay]);

  useFrame(() => {
    if (!shouldRender) return;
    const scene = getSceneObject(sceneName);
    if (!scene) {
      // Scene not found yet - keep positions at default [0,0,0] until scene is available
      // This is expected during initial load - labels will appear once scene is registered
      // For TV scene, registration may be delayed by video texture loading
      return;
    }

    // Derive a robust base position
    const base = new THREE.Vector3();
    if (getBasePosition) {
      // Use custom position function if provided
      const customPos = getBasePosition(scene);
      if (customPos && Array.isArray(customPos)) {
        base.fromArray(customPos);
      } else {
        scene.getWorldPosition(base);
      }
    } else if (sceneName === 'phonebox-scene') {
      // Use phone-specific positioning for phonebox scene
      const handsetWorld = getPhonePosition(scene);
      if (handsetWorld && Array.isArray(handsetWorld)) {
        base.fromArray(handsetWorld);
      } else {
        scene.getWorldPosition(base);
      }
    } else {
      // For other scenes, use scene world position
      scene.getWorldPosition(base);
    }

    // Offset toward camera, clamped as a fraction of distance
    const cameraWorld = new THREE.Vector3();
    camera.getWorldPosition(cameraWorld);
    // Move a fixed amount toward the camera to control world-space proximity
    const camDir = cameraWorld.clone().sub(base).normalize();
    const toCamera = camDir.multiplyScalar(depthOffset);

    // Calculate horizontal direction relative to camera view (not scene rotation)
    // Use camera's local coordinate system to ensure consistent left/right positioning
    const cameraRight = new THREE.Vector3(1, 0, 0);
    cameraRight.applyQuaternion(camera.quaternion);
    cameraRight.normalize();
    
    // Use camera's local up vector for consistent vertical positioning
    const cameraUp = new THREE.Vector3(0, 1, 0);
    cameraUp.applyQuaternion(camera.quaternion);
    cameraUp.normalize();
    
    // Use camera's coordinate system (ensures left is always on the left from camera's perspective)
    const right = cameraRight;
    const up = cameraUp;

    const leftWorld = base
      .clone()
      .add(right.clone().multiplyScalar(-horizontalOffset))
      .add(up.clone().multiplyScalar(verticalOffset))
      .add(toCamera);
    const rightWorld = base
      .clone()
      .add(right.clone().multiplyScalar(horizontalOffset))
      .add(up.clone().multiplyScalar(verticalOffset))
      .add(toCamera);

    // Throttle position updates to prevent multiple HTML renders during camera movement
    const now = performance.now();
    if (now - lastUpdateRef.current >= updateThrottle) {
      setLeftPos(leftWorld.toArray());
      setRightPos(rightWorld.toArray());
      lastUpdateRef.current = now;
    }

    // Billboard the groups to face the camera
    if (leftGroupRef.current) {
      leftGroupRef.current.quaternion.copy(camera.quaternion);
    }
    if (rightGroupRef.current) {
      rightGroupRef.current.quaternion.copy(camera.quaternion);
    }
  });

  // Default rightChildren to sound toggle if not provided
  const defaultRightChildren = (
    <LabelledToggle
      label="SOUND"
      isActive={isSoundOn}
      onToggle={toggleSound}
      offLabel="OFF"
      onLabel="ON"
      ariaLabel="Toggle sound"
    />
  );

  if (!shouldRender) return null;

  // Shared animation style for both labels to ensure they animate together
  const animationDuration = '0.5s';
  const sharedStyle = {
    opacity: shouldShow ? 1 : 0,
    transition: shouldShow ? `opacity ${animationDuration} ease-in-out` : 'none',
    visibility: shouldShow ? 'visible' : 'hidden'
  };

  const labelStyle = {
    color: '#FFF',
    fontFamily: "'Generic-G80-RD-Standard-DEMO', 'PP Mori', -apple-system, Roboto, Helvetica, sans-serif",
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: '34px',
    letterSpacing: '2%',
    ...sharedStyle
  };

  // Use transform mode so distanceFactor affects perspective scaling
  const df = 10;
  const uiScale = 1; // additional CSS scale for consistent readability

  return (
    <>
      <group ref={leftGroupRef} position={leftPos}>
        <Html transform distanceFactor={df}>
          <div style={{ transform: `scale(${uiScale})`, transformOrigin: 'center', pointerEvents: 'none' }}>
            <div style={labelStyle}>{leftChildren}</div>
          </div>
        </Html>
      </group>

      <group ref={rightGroupRef} position={rightPos}>
        <Html transform distanceFactor={df}>
          <div style={{ 
            transform: `scale(${uiScale})`, 
            transformOrigin: 'center',
            pointerEvents: 'auto',
            ...sharedStyle
          }}>
            {rightChildren !== undefined ? rightChildren : defaultRightChildren}
          </div>
        </Html>
      </group>
    </>
  );
};

export default React.memo(CameraAlignedLabels);

